import React, { useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { useUnsavedChangesPrompt } from 'hooks/useUnsavedChangesPrompt';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { FormProvider, useForm } from 'react-hook-form';
import { PAGES } from 'constants/pages';
import { useHasPermission } from 'hooks/useHasPermission';

import {
    useAttachLastStripePaymentMethodMutation,
    useGetActiveOrganizationsQuery,
    useGetSitesQuery,
    useGetUserQuery,
    TotadminPermission,
    UserState,
    useUpdateAdminUserMutation,
} from 'data/__generated__';
import { getApiUrl } from 'data/connection/graphql-utils';

import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import { CopyValue } from 'components/CopyValue';
import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { TotemLabel } from 'components/TotemLabel';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { PaymentsSection } from 'pages/Payments/components/PaymentsSection';
import { IdentitySection, LastTransactions, NotificationsSection, PaymentMethods } from '../components';
import { PermissionsEditor } from '../components/PermissionsEditor';

type ParamTypes = {
    userId: string;
};

type PermissionFormField = {
    totemId: string | null;
    totemFullname: string | null;
    userGroups: string[];
};

type VisitedSiteFormField = {
    _id: string;
    name: string;
    visitedAt: number;
};

type UserEmailFormField = {
    address: string;
    verified: boolean;
};

export type UserDetailsFormData = {
    permissions: PermissionFormField[];
    organizationId: string | null;
    state: UserState;
    userGroupIds: string[];
    visitedSites: VisitedSiteFormField[];
    emails: UserEmailFormField[];
    username: string;
    defaultTruckColumnId: string | null;
};

export const UserDetails = () => {
    const { userId = '' } = useParams<ParamTypes>();
    const canManagePermissions = useHasPermission(TotadminPermission.PermissionsManage);

    const methods = useForm<UserDetailsFormData>();
    const {
        handleSubmit,
        formState: { isDirty },
        reset,
    } = methods;

    const [updateUser, { loading: updateUserLoading }] = useUpdateAdminUserMutation();

    const {
        loading: userLoading,
        data: userData,
        error: userError,
    } = useGetUserQuery({ variables: { userId }, skip: !userId });

    const {
        loading: organizationsLoading,
        data: organizationsData,
        error: organizationsError,
    } = useGetActiveOrganizationsQuery();

    const { loading: sitesLoading, data: sitesData, error: sitesError } = useGetSitesQuery();

    const [attachLastStripePaymentMethod, { loading: attachLastStripePaymentMethodLoading }] =
        useAttachLastStripePaymentMethodMutation();

    const userFromAdmin = userData?.userFromAdmin;

    useEffect(() => {
        if (!userFromAdmin) {
            return;
        }
        const permissionsMap = new Map<string | null, { userGroups: string[]; totemFullname: string | null }>();
        for (const p of userFromAdmin.permissions) {
            const key = p.totemId ?? null;
            if (!permissionsMap.has(key)) {
                permissionsMap.set(key, { userGroups: [], totemFullname: p.totem?.fullname ?? null });
            }
            permissionsMap.get(key)!.userGroups.push(p.userGroup);
        }
        const globalGroups = permissionsMap.get(null)?.userGroups ?? [];
        const sitePermissions = [...permissionsMap.entries()]
            .filter(([key]) => key !== null)
            .map(([totemId, { userGroups, totemFullname }]) => ({ totemId, totemFullname, userGroups }));

        reset({
            permissions: [{ totemId: null, totemFullname: null, userGroups: globalGroups }, ...sitePermissions],
            state: userFromAdmin.profile.state,
            username: userFromAdmin.username,
            organizationId: userFromAdmin.organization?._id,
            userGroupIds: userFromAdmin.userGroupIds,
            visitedSites: userFromAdmin.visitedSites || [],
            emails: userFromAdmin.emails,
            defaultTruckColumnId: userFromAdmin.defaultTruckColumnId ?? null,
        });
    }, [userFromAdmin, reset]);

    useUnsavedChangesPrompt({
        message: 'Êtes-vous sûr.e de vouloir quitter cette page sans enregistrer ?',
        when: ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
    });

    if (organizationsLoading || sitesLoading || userLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (userError || !userFromAdmin || organizationsError || !organizationsData || sitesError || !sitesData) {
        throw new Error("Une erreur est survenue lors de la récupération de l'utilisateur");
    }

    const { activeOrganizations: organizations } = organizationsData;
    const { sites } = sitesData;

    const { _id, getLastTransactions: lastTransactions, wallet } = userFromAdmin;

    const {
        profile: { firstname, lastname },
    } = userFromAdmin;

    const retrieveLastStripePaymentMethod = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        const { data } = await attachLastStripePaymentMethod({ variables: { userId } });
        const error = data?.attachLastStripePaymentMethod.error;
        const success = data?.attachLastStripePaymentMethod.success;
        if (error || !success) {
            toast.error(error || 'Erreur inconnue');
        }
    };

    const onSubmit = handleSubmit(async (fieldsToUpdate) => {
        const { data } = await updateUser({
            variables: {
                fieldsToUpdate: {
                    userGroupIds: fieldsToUpdate.userGroupIds || [],
                    permissions: fieldsToUpdate.permissions.flatMap((p) =>
                        p.userGroups.map((group) => ({
                            totemId: p.totemId,
                            userGroup: group,
                        })),
                    ),
                    state: fieldsToUpdate.state,
                    visitedSites: fieldsToUpdate.visitedSites.map((site) => ({
                        _id: site._id,
                        visitedAt: site.visitedAt,
                    })),
                    organizationId: fieldsToUpdate.organizationId,
                    emails: fieldsToUpdate.emails.map((email) => ({
                        address: email.address,
                    })),
                    username: fieldsToUpdate.username,
                    defaultTruckColumnId: fieldsToUpdate.defaultTruckColumnId ?? null,
                },
                userId,
            },
        });

        if (data) {
            const {
                updateAdminUser: { success, error },
            } = data;
            if (success) {
                toast.success(`L'utilisateur ${firstname} ${lastname} a bien été modifiée`);
            } else {
                if (error) {
                    toast.error(error);
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/users' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.userDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateUserLoading}
                            saveTitle="Mettre à jour"
                            saveDataTest="submit-button"
                            saveRequiredPermissions={[TotadminPermission.UsersManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <SectionContainer title="Identité" isInitiallyOpen>
                                <SectionColumn>
                                    <IdentitySection user={userFromAdmin} organizations={organizations} sites={sites} />
                                </SectionColumn>
                            </SectionContainer>
                            <SectionContainer
                                linkToMore={`/transactions?userIds=${_id}`}
                                title="Dernières transactions"
                                isInitiallyOpen
                            >
                                <SectionColumn>
                                    <LastTransactions lastTransactions={lastTransactions} />
                                </SectionColumn>
                            </SectionContainer>
                            <SectionContainer
                                linkToMore={`/payments?userIds=${_id}`}
                                title="Paiements"
                                isInitiallyOpen
                                requiredPermissions={[TotadminPermission.TransactionsRead]}
                            >
                                <SectionColumn>
                                    <PaymentsSection
                                        scope={{ userIds: [_id] }}
                                        hiddenColumnIds={['userFullName']}
                                        emptyMessage="Aucun paiement pour cet utilisateur"
                                        dataTest="user-payments"
                                    />
                                </SectionColumn>
                            </SectionContainer>
                            <SectionContainer
                                title="Moyens de paiement"
                                isInitiallyOpen
                                requiredPermissions={[TotadminPermission.CreditsRead]}
                            >
                                <SectionColumn>
                                    <PaymentMethods userId={userId} wallet={wallet} />
                                    <TotemLabel>Lien d'ajout de CB</TotemLabel>
                                    <CopyValue value={`${getApiUrl()}/stripe/add/${_id}`} />
                                    <TotemPrimaryLoadingButton
                                        loading={attachLastStripePaymentMethodLoading}
                                        useIcon={false}
                                        label="récupérer le dernier moyen de paiement sur Stripe"
                                        data-test="submit-button"
                                        $minWidth="140px"
                                        type="submit"
                                        onClick={retrieveLastStripePaymentMethod}
                                        requiredPermissions={[TotadminPermission.OrganizationsManage]}
                                    />
                                </SectionColumn>
                            </SectionContainer>
                            <SectionContainer title="Permissions" isInitiallyOpen>
                                <SectionColumn>
                                    <PermissionsEditor disabled={!canManagePermissions} />
                                </SectionColumn>
                            </SectionContainer>
                            {/* La section ne contient que des interrupteurs, chacun une écriture immédiate (`users_manage`). */}
                            <SectionContainer
                                title="Notifications"
                                requiredPermissions={[TotadminPermission.UsersManage]}
                            >
                                <SectionColumn>
                                    <NotificationsSection user={userFromAdmin} />
                                </SectionColumn>
                            </SectionContainer>
                            <AuditLogsSection collectionDocumentIds={[_id]} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;
